import {Injectable} from '@angular/core';
import {Container, Image} from '../model/layout.model';
import {DocumentModel} from '../model/document.model';
import {EventBusService} from './event-bus.service';
import {EventBusTypes} from '../model/constants';
import {GrafikUmfang, WarnschildSpiegel} from '../model/event-bus.model';

/** Die andere Seite eines gespiegelten Warnschilds: Karte und Box. */
export interface WarnschildPartner {
  containerId: string;
  boxId: string;
}

/**
 * Gespiegelte Warnschilder (05.11.2026, Screenshot der Redaktion): die Warnschild-Kästchen der Zeile
 * "Tag" der Lokalzeit-Prognose-Karte (`lz_<stadt>_prog_tag<N>_tag_warn`, N = 2/3/4) zeigen dasselbe
 * wie das Warnschild `wert` rechts unten auf der Karte 3 "alle Regionen" des Aussichtentags N-1
 * (`wdr.lokalzeit.<stadt>.<N-1>-aussichtentag-ganz.karte3`) – und umgekehrt: wer eine Seite ändert,
 * ändert beide. Die Kästchen der Zeile "Nacht" haben keinen Partner (freie Eingabe). "Lokalzeit am
 * Samstag" hat weder Prognose-Karte noch Aussichtentage, dort gibt es nichts zu spiegeln.
 *
 * Ablauf: die `ImageComponent` schickt bei jeder Änderung einer Box mit Partner
 * (`partnerVon`, auch beim "leeren") ein `WARNSCHILD_SPIEGEL_EVENT`; `DocumentDetailComponent`
 * – die das Dokument hat, die Partner-Karte liegt in einem anderen Reiter – ruft {@link spiegle}:
 * der Wert geht in `image.values` der Partner-Karte und ein `GRAFIK_WERTE_GEAENDERT_EVENT` lässt eine
 * gerenderte Partner-Karte neu aus dem Dokument laden. Steht dort schon derselbe Wert, passiert
 * nichts – so kann das Spiegeln nicht hin- und herspringen (das Neuladen aus dem Dokument schickt
 * ohnehin kein Ereignis, nur Eingabe und "leeren" tun das).
 */
@Injectable({providedIn: 'root'})
export class WarnschildSpiegelService {

  /** Das Warnschild der Karten: Box-Id `wert`. */
  static readonly WARNSCHILD = 'wert';
  private static readonly PROGNOSE = /^wdr\.lokalzeit\.([a-z-]+)\.prognose\.karte1$/;
  private static readonly TAG_WARN = /^lz_([a-z_]+)_prog_tag([234])_tag_warn$/;
  private static readonly AUSSICHTENTAG = /^wdr\.lokalzeit\.([a-z-]+)\.([123])-aussichtentag-ganz\.karte3$/;

  constructor(private eventBus: EventBusService) {
  }

  /** Partner-Box eines gespiegelten Warnschilds, oder null, wenn die Box keinen Partner hat. */
  static partnerVon(containerId: string | null | undefined, boxId: string | null | undefined): WarnschildPartner | null {
    if (!containerId || !boxId) {
      return null;
    }
    const prognose = containerId.match(WarnschildSpiegelService.PROGNOSE);
    if (prognose) {
      const box = boxId.match(WarnschildSpiegelService.TAG_WARN);
      if (!box || box[1] !== WarnschildSpiegelService.idTeil(prognose[1])) {
        return null;
      }
      return {containerId: `wdr.lokalzeit.${prognose[1]}.${Number(box[2]) - 1}-aussichtentag-ganz.karte3`,
        boxId: WarnschildSpiegelService.WARNSCHILD};
    }
    const aussicht = containerId.match(WarnschildSpiegelService.AUSSICHTENTAG);
    if (aussicht && boxId === WarnschildSpiegelService.WARNSCHILD) {
      return {containerId: `wdr.lokalzeit.${aussicht[1]}.prognose.karte1`,
        boxId: `lz_${WarnschildSpiegelService.idTeil(aussicht[1])}_prog_tag${Number(aussicht[2]) + 1}_tag_warn`};
    }
    return null;
  }

  /** Stadt-Segment der containerId → Teil der Box-Id (Bindestriche zu Unterstrichen, wie der Generator). */
  private static idTeil(stadt: string): string {
    return stadt.replace(/-/g, '_');
  }

  /**
   * Schreibt den geänderten Wert in die Partner-Karte im Dokument und meldet die Karte als geändert.
   *
   * @returns true, wenn die Partner-Karte geändert wurde
   */
  spiegle(document: DocumentModel | null | undefined, ereignis: WarnschildSpiegel): boolean {
    const partner = WarnschildSpiegelService.partnerVon(ereignis.containerId, ereignis.boxId);
    if (!partner) {
      return false;
    }
    const karte = WarnschildSpiegelService.finde(document?.tabs, partner.containerId);
    if (!karte) {
      console.warn(`WARN: Warnschild-Spiegel: Partner-Karte ${partner.containerId} nicht im Dokument`);
      return false;
    }
    const neu = ereignis.wert ?? '';
    if ((karte.values?.[partner.boxId] ?? '') === neu) {
      return false;
    }
    // Ein leerer Wert löscht den Schlüssel – wie rememberValue() in der ImageComponent: eine leere
    // Box steht nicht als Leerstring im Datensatz.
    const werte = {...(karte.values ?? {})};
    if (neu === '') {
      delete werte[partner.boxId];
    } else {
      werte[partner.boxId] = neu;
    }
    karte.values = werte;
    this.eventBus.emit(EventBusTypes.GRAFIK_WERTE_GEAENDERT_EVENT.valueOf(), new GrafikUmfang([partner.containerId]));
    return true;
  }

  private static finde(container: Container[] | undefined, containerId: string): Image | null {
    for (const c of container ?? []) {
      if (c.containerId === containerId && c.containerType === 'Image') {
        return c as Image;
      }
      const treffer = WarnschildSpiegelService.finde(c.containerList, containerId);
      if (treffer) {
        return treffer;
      }
    }
    return null;
  }
}
